import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, Music, Shield, AlertCircle, ArrowLeft, Check } from 'lucide-react';

const Terms = () => {
    return (
        <div className="min-h-screen bg-hero">
            {/* Header */}
            <section className="py-12 px-6 bg-gradient-to-b from-gray-900/80 to-transparent">
                <div className="max-w-4xl mx-auto">
                    <Link 
                        to="/"
                        className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors duration-300 mb-8"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Home
                    </Link>
                    
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-6">
                            <FileText className="w-16 h-16 text-blue-400" />
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black gradient-text mb-6">
                            Terms of Service
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                            Understanding the terms and conditions for using The Deep Wave arts and sound ensemble platform.
                        </p>
                        <p className="text-sm text-gray-500 mt-4">
                            Last updated: January 2024
                        </p>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="space-y-12">
                        
                        {/* Acceptance of Terms */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Check className="w-8 h-8 text-green-400" />
                                <h2 className="text-2xl font-bold gradient-text">Acceptance of Terms</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <p className="text-gray-400">
                                    By accessing and using The Deep Wave platform, you agree to be bound by these Terms of Service 
                                    and all applicable laws and regulations. If you do not agree with any of these terms, 
                                    you are prohibited from using or accessing this platform.
                                </p>
                                <p className="text-gray-400">
                                    These terms constitute a legally binding agreement between you and The Deep Wave arts and 
                                    sound ensemble platform. We reserve the right to modify these terms at any time, 
                                    and such modifications will be effective immediately upon posting.
                                </p>
                            </div>
                        </div>

                        {/* Platform Use */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Users className="w-8 h-8 text-blue-400" />
                                <h2 className="text-2xl font-bold gradient-text">Platform Use & Account Responsibilities</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Account Registration</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• You must provide accurate and complete information when creating an account</li>
                                        <li>• You are responsible for maintaining the security of your account credentials</li>
                                        <li>• One account per person; multiple accounts are not permitted</li>
                                        <li>• You must be at least 13 years old to create an account</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Acceptable Use</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Use the platform for creative and artistic purposes in good faith</li>
                                        <li>• Respect other users and maintain a positive community environment</li>
                                        <li>• Follow all applicable local, state, and federal laws</li>
                                        <li>• Engage authentically and avoid spam or misleading behavior</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Prohibited Activities</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Uploading content that infringes on intellectual property rights</li>
                                        <li>• Harassment, hate speech, or discriminatory behavior</li>
                                        <li>• Attempting to hack, spam, or disrupt platform services</li>
                                        <li>• Impersonating other users or providing false information</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Content & Intellectual Property */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Music className="w-8 h-8 text-purple-400" />
                                <h2 className="text-2xl font-bold gradient-text">Content & Intellectual Property</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Your Content Rights</h3>
                                    <p className="text-gray-400">
                                        You retain all rights to content you create and upload to The Deep Wave platform. 
                                        By uploading content, you grant us a non-exclusive, royalty-free license to display, 
                                        distribute, and promote your content within the platform and for marketing purposes.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Content Standards</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• All uploaded content must be your original work or used with proper permission</li>
                                        <li>• Content must not violate any copyrights, trademarks, or other intellectual property rights</li>
                                        <li>• Explicit or inappropriate content must be clearly labeled and age-appropriate</li>
                                        <li>• Content must comply with community guidelines and artistic standards</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Content Moderation</h3>
                                    <p className="text-gray-400">
                                        We reserve the right to review, moderate, or remove content that violates these terms 
                                        or community standards. We may also suspend or terminate accounts for repeated violations.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">DMCA Compliance</h3>
                                    <p className="text-gray-400">
                                        We respect intellectual property rights and comply with the Digital Millennium Copyright Act. 
                                        If you believe your content has been used without permission, please contact our support team 
                                        with a detailed DMCA notice.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Artist Features & Revenue */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Music className="w-8 h-8 text-yellow-400" />
                                <h2 className="text-2xl font-bold gradient-text">Artist Features & Revenue Sharing</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Artist Status</h3>
                                    <p className="text-gray-400">
                                        Users may enable artist status to access additional features including media uploads, 
                                        enhanced profiles, and revenue-generating capabilities. Artist status comes with 
                                        additional responsibilities and content quality standards.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Revenue & Payments</h3>
                                    <ul className="space-y-2 text-gray-400">
                                        <li>• Artists may earn revenue through downloads, tips, and featured content placements</li>
                                        <li>• Platform fees may apply to revenue transactions as outlined in payment terms</li>
                                        <li>• Artists are responsible for reporting income for tax purposes</li>
                                        <li>• Payment processing is handled by third-party providers subject to their terms</li>
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Collaborative Features</h3>
                                    <p className="text-gray-400">
                                        The platform supports collaborative works and contributing artist credits. 
                                        All participating artists must agree to collaboration terms before content publication. 
                                        Revenue sharing for collaborative works must be agreed upon by all contributors.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Privacy & Data */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Shield className="w-8 h-8 text-cyan-400" />
                                <h2 className="text-2xl font-bold gradient-text">Privacy & Data Protection</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Data Collection</h3>
                                    <p className="text-gray-400">
                                        Our data collection and usage practices are governed by our Privacy Policy. 
                                        By using the platform, you consent to the collection and use of information 
                                        as described in our Privacy Policy.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Analytics & Tracking</h3>
                                    <p className="text-gray-400">
                                        We collect analytics data to improve platform performance and provide creator insights. 
                                        This includes play counts, engagement metrics, and user behavior data.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Data Security</h3>
                                    <p className="text-gray-400">
                                        We implement security measures to protect user data, but cannot guarantee absolute security. 
                                        Users are responsible for maintaining the confidentiality of their account information.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Disclaimers & Limitations */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <AlertCircle className="w-8 h-8 text-red-400" />
                                <h2 className="text-2xl font-bold gradient-text">Disclaimers & Limitations</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Service Availability</h3>
                                    <p className="text-gray-400">
                                        While we strive for 100% uptime, we do not guarantee uninterrupted service availability. 
                                        The platform may be temporarily unavailable due to maintenance, updates, or technical issues.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Content Backup</h3>
                                    <p className="text-gray-400">
                                        While we maintain backups, users are responsible for keeping their own copies of important content. 
                                        We are not liable for any data loss, though we make reasonable efforts to prevent it.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Third-Party Services</h3>
                                    <p className="text-gray-400">
                                        The platform may integrate with third-party services (payment processors, analytics, etc.). 
                                        These services have their own terms and privacy policies that apply to their use.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Limitation of Liability</h3>
                                    <p className="text-gray-400">
                                        To the maximum extent permitted by law, The Deep Wave platform and its operators 
                                        shall not be liable for any indirect, incidental, special, or consequential damages 
                                        arising from platform use.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Account Termination */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <AlertCircle className="w-8 h-8 text-orange-400" />
                                <h2 className="text-2xl font-bold gradient-text">Account Termination</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Voluntary Termination</h3>
                                    <p className="text-gray-400">
                                        You may terminate your account at any time through your account settings. 
                                        Upon termination, your content will be removed from the platform, though some 
                                        data may be retained for legal or security purposes.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Platform Termination</h3>
                                    <p className="text-gray-400">
                                        We may suspend or terminate accounts that violate these terms, engage in abusive behavior, 
                                        or pose security risks to the platform or other users. Serious violations may result in 
                                        immediate termination without prior notice.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Effect of Termination</h3>
                                    <p className="text-gray-400">
                                        Upon account termination, your access to the platform will cease, uploaded content will be removed, 
                                        and any pending revenue payments will be processed according to our payment terms.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Changes & Contact */}
                        <div className="glass-card p-8">
                            <div className="flex items-center gap-3 mb-6">
                                <FileText className="w-8 h-8 text-blue-400" />
                                <h2 className="text-2xl font-bold gradient-text">Changes to Terms & Contact Information</h2>
                            </div>
                            <div className="space-y-4 text-gray-300">
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Terms Updates</h3>
                                    <p className="text-gray-400">
                                        These terms may be updated from time to time to reflect changes in our services, 
                                        legal requirements, or business practices. Users will be notified of significant 
                                        changes via email and platform notifications.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Governing Law</h3>
                                    <p className="text-gray-400">
                                        These terms are governed by the laws of the State of Florida, United States. 
                                        Any disputes will be resolved in the appropriate courts of Florida.
                                    </p>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white mb-2">Questions & Support</h3>
                                    <p className="text-gray-400">
                                        If you have questions about these terms or need clarification on any policies, 
                                        please contact our support team for assistance.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <Link 
                                        to="/support" 
                                        className="btn-primary flex items-center gap-2 justify-center"
                                    >
                                        <FileText className="w-5 h-5" />
                                        Contact Support
                                    </Link>
                                    <Link 
                                        to="/privacy" 
                                        className="btn-ghost flex items-center gap-2 justify-center"
                                    >
                                        View Privacy Policy
                                    </Link>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default Terms;